import React, { useState } from 'react';
import { Star, Fuel, Wrench, Shield, Route, Factory, IdCard, CheckCircle2, Ban, Warehouse } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { VEHICLES } from '../../data/vehicles';
import { MANUFACTURER_FINANCE } from '../../data/manufacturerFinance';
import { describeVehicleAccessibility, VEHICLE_LICENSE_LABELS, getLicenseLabel } from '../../data/vehicleAccessibility';
import { localize } from '../../data/i18nData';
import useGameStore from '../../store/gameStore';
import { garageExpansionRequired } from '../../store/slices/fleetSlice';
import ManufacturerFinanceModal from './ManufacturerFinanceModal';

export function BuyTab({ vehicles, treasury, canUnlock, buyVehicle, playSound }) {
  const { t } = useTranslation();
  const [mfgTarget, setMfgTarget] = useState(null);
  // 14/06/2026 — garage de base = 2 places : le 3e camion exige
  // l'Agrandissement dépôt (x2) côté investissements QG.
  const garageBlocked = useGameStore(garageExpansionRequired);
  return (
    <div className="space-y-3">
      {garageBlocked && (
        <div className="bg-warn/10 border border-warn/40 rounded-lg px-3 py-2.5 flex items-start gap-2" data-testid="garage-capacity-banner">
          <Warehouse className="w-4 h-4 text-warn flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-warn">{t('fleet_garage_full_title')}</p>
            <p className="text-[11px] text-slate-300 mt-0.5">{t('fleet_garage_full_body')}</p>
          </div>
        </div>
      )}
      {[1, 2, 3, 4, 5].map(tier => {
        const tierVehicles = VEHICLES.filter(v => v.tier === tier);
        return (
          <div key={tier}>
            <h3 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-1">
              <Star className="w-4 h-4 text-warn" /> Tier {tier}
              <span className="text-xs text-slate-500 ml-1">({tierVehicles.length} {t('models')})</span>
            </h3>
            <div className="space-y-2">
              {tierVehicles.map(rawV => {
                const v = localize('vehicle', rawV);
                const unlocked = canUnlock(rawV);
                const canAfford = treasury >= v.price;
                const ownedCount = vehicles.filter(ov => ov.dataId === v.id).length;
                return (
                  <div key={v.id} className={`bg-ink-800 rounded-lg border p-4 ${unlocked ? 'border-ink-600' : 'border-ink-600 opacity-60'}`} data-testid={`buy-vehicle-${v.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {v.image && (
                          <img src={v.image} alt={`${v.brand} ${v.model}`} className="w-28 h-[72px] rounded-lg object-cover border border-ink-600 flex-shrink-0" />
                        )}
                        <div>
                          <h4 className="font-bold text-slate-100 flex items-center gap-1.5 flex-wrap">
                            {v.brand} {v.model}
                            {ownedCount > 0 && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-money/15 text-money font-mono font-bold border border-money/30" data-testid={`owned-count-${v.id}`}>
                                ×{ownedCount} en flotte
                              </span>
                            )}
                          </h4>
                          <p className="text-xs text-slate-500">{v.ptac} — {v.specialties.join(', ')}</p>
                          <p className="text-xs text-slate-500 mt-1">{v.advantage}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-0.5"><Fuel className="w-3 h-3" /> {v.consumption}L/100</span>
                            <span className="flex items-center gap-0.5"><Route className="w-3 h-3" /> {v.kmPerDay} km/j</span>
                            <span className="flex items-center gap-0.5"><Wrench className="w-3 h-3" /> {formatMoney(v.maintenancePerMonth)}/m</span>
                            <span className="flex items-center gap-0.5"><Shield className="w-3 h-3" /> {formatMoney(v.insurancePerMonth)}/m</span>
                          </div>
                          {/* Permis required — shown on EVERY vehicle (including B) so the user has a clear signal. */}
                          {(() => {
                            const lic = getLicenseLabel(v.license);
                            return (
                              <p
                                className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold"
                                style={{ color: lic.color, backgroundColor: `${lic.color}15`, borderColor: `${lic.color}40` }}
                                data-testid={`vehicle-license-${v.id}`}
                                title={lic.full}
                              >
                                <IdCard className="w-3 h-3" /> {lic.code}
                                <span className="text-[9px] font-normal opacity-80 ml-0.5">— {lic.full}</span>
                              </p>
                            );
                          })()}
                          {/* Pedagogical block — clearly lists accessible contract types
                              and what this truck CANNOT carry. Helps the user pick the
                              right tier instead of guessing from `specialties`. */}
                          {(() => {
                            const acc = describeVehicleAccessibility(v);
                            return (
                              <details className="mt-2 group" data-testid={`vehicle-access-${v.id}`}>
                                <summary className="cursor-pointer text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-200 transition-colors">
                                  Quels contrats ce camion prend-il ?
                                </summary>
                                <div className="mt-1.5 pl-1 space-y-1">
                                  <p className="text-[10px] text-slate-500 italic">{acc.hint}</p>
                                  <ul className="text-[10px] space-y-0.5">
                                    {acc.accessible.map(item => (
                                      <li key={`ok-${item}`} className="flex items-start gap-1 text-emerald-300">
                                        <CheckCircle2 className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                      </li>
                                    ))}
                                    {acc.blocked.map(item => (
                                      <li key={`ko-${item}`} className="flex items-start gap-1 text-rose-300/70">
                                        <Ban className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </details>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="text-lg font-bold text-foreground">{formatMoney(v.price)}</p>
                        {v.engineType === 'electric' && (
                          <p
                            className="text-[10px] text-emerald-300 mt-1 inline-flex items-center gap-1 font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30"
                            data-testid={`electric-subsidy-badge-${v.id}`}
                            title="Prime de conversion appliquée à l'achat"
                          >
                            −10 000 € prime État
                          </p>
                        )}
                        {!unlocked && (
                          <p className="text-xs text-danger mt-1 max-w-[120px]">{v.unlockedIf}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                      {MANUFACTURER_FINANCE[v.brand] && unlocked && !garageBlocked && (
                        <button
                          onClick={() => setMfgTarget(v)}
                          className="text-xs border-2 px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-1.5"
                          style={{
                            borderColor: MANUFACTURER_FINANCE[v.brand].color + '60',
                            color: MANUFACTURER_FINANCE[v.brand].color,
                          }}
                          data-testid={`mfg-finance-${v.id}`}
                          title={`Financement direct via ${MANUFACTURER_FINANCE[v.brand].name}`}
                        >
                          <Factory className="w-3 h-3" />
                          Financement {v.brand}
                        </button>
                      )}
                      <button
                        onClick={() => { playSound('buy'); buyVehicle(v.id); }}
                        disabled={!unlocked || !canAfford || garageBlocked}
                        className="text-xs bg-accent text-white px-4 py-2 rounded-md font-medium disabled:opacity-40 hover:bg-accent-hover transition-colors"
                        data-testid={`buy-btn-${v.id}`}
                      >
                        {garageBlocked && unlocked ? t('fleet_garage_full_btn') : !canAfford && unlocked ? t('insufficient_funds') : unlocked ? (ownedCount > 0 ? `${t('buy_vehicle')} (+1)` : t('buy_vehicle')) : t('locked')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {mfgTarget && (
        <ManufacturerFinanceModal vehicleData={mfgTarget} onClose={() => setMfgTarget(null)} />
      )}
    </div>
  );
}
